import { useRef, useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'
import { Drawer } from '../common/overlays/Drawer'
import { Badge } from '../common/primitives/Badge'
import { Button } from '../common/primitives/Button'
import { SearchField } from '../common/forms/SearchField'
import styles from './Patterns.module.css'

export function FilterBar({
  query = '',
  onQueryChange,
  onSearch,
  searchPlaceholder = 'Search…',
  searchLabel = 'Search',
  loading = false,
  controls,
  advancedFilters,
  advancedMode = 'responsive',
  activeFilters = [],
  onRemoveFilter,
  onClearAll,
  serialize,
  label = 'Filters',
  drawerTitle = 'Filters',
  className = '',
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const rootRef = useRef(null)
  const focusStableControl = () => window.queueMicrotask(() => {
    const target = advancedInDrawer
      ? triggerRef.current
      : rootRef.current?.querySelector('input[type="search"]')
    target?.focus()
  })
  const serialized = serialize?.({ query, activeFilters })
  const advancedInDrawer = advancedMode === 'drawer'
  const inlineAdvancedFilters = advancedInDrawer ? null : advancedFilters
  const hasInlineControls = Boolean(controls || inlineAdvancedFilters)

  return (
    <section ref={rootRef} className={`${styles.filterBar} ${className}`.trim()} aria-label={label} data-serialized-filters={serialized}>
      <div className={styles.filterMain}>
        <SearchField
          className={styles.filterSearch}
          value={query}
          onChange={onQueryChange}
          onSubmit={onSearch}
          placeholder={searchPlaceholder}
          label={searchLabel}
          loading={loading}
        />
        {hasInlineControls ? <div className={styles.filterControls}>{controls}{inlineAdvancedFilters}</div> : null}
        {advancedFilters ? (
          <Button
            ref={triggerRef}
            className={`${styles.filterDrawerButton} ${advancedInDrawer ? styles.filterDrawerButtonVisible : ''}`.trim()}
            variant="secondary"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <SlidersHorizontal size={16} aria-hidden="true" />
            Filters
            {activeFilters.length ? <Badge compact tone="info">{activeFilters.length}</Badge> : null}
          </Button>
        ) : null}
      </div>
      {activeFilters.length ? (
        <div className={styles.activeFilters}>
          <span className={styles.filterSummary}>{activeFilters.length} active {activeFilters.length === 1 ? 'filter' : 'filters'}</span>
          {activeFilters.map((filter) => (
            onRemoveFilter ? (
              <Button
                key={filter.key || filter.label}
                className={styles.filterChip}
                size="compact"
                variant="ghost"
                onClick={() => {
                  onRemoveFilter(filter.key)
                  focusStableControl()
                }}
                aria-label={`Remove ${filter.label} filter`}
              >
                {filter.label}
                <X size={14} aria-hidden="true" />
              </Button>
            ) : <Badge key={filter.key || filter.label}>{filter.label}</Badge>
          ))}
          {onClearAll ? <Button size="compact" variant="ghost" onClick={() => { onClearAll(); focusStableControl() }}>Clear all</Button> : null}
        </div>
      ) : null}
      <Drawer
        open={open}
        title={drawerTitle}
        side="right"
        onClose={() => setOpen(false)}
        returnFocusRef={triggerRef}
        footer={(
          <>
            {activeFilters.length && onClearAll ? (
              <Button variant="ghost" onClick={() => { onClearAll(); setOpen(false) }}>Clear all</Button>
            ) : null}
            <Button variant="primary" loading={loading} onClick={() => setOpen(false)}>Show results</Button>
          </>
        )}
      >
        {advancedFilters}
      </Drawer>
    </section>
  )
}
